/**
 * Portfolio API
 *
 * RTK Query endpoints for portfolio management.
 */

import { api } from '../api';
import type { PublicEndorsement } from '../../components/endorsements/endorsementTypes';

// Types
export type RateType = 'none' | 'hour' | 'session' | 'project';
/** `conversation_result`: a coaching result added from its result view (private until published). */
export type SourceType = 'practice_session' | 'coaching_session' | 'upload' | 'conversation_result';

/** What a coaching result item was judged on, read from the canonical result. */
export interface PortfolioResultProvenance {
  sessionId: string;
  rubric: { id: string; version: string; title: string | null } | null;
  evidenceClasses: string[];
  overall: number | null;
  evaluatedAt: string | null;
}

/**
 * What a PUBLISHED result item shows publicly — the same fields a share link
 * shows (rubric, evidence classes, overall; null overall = not measured).
 */
export interface PublicResultView {
  rubric: { id: string; version: string; title: string | null } | null;
  evidenceClasses: string[];
  overall: number | null;
}

/** A published portfolio item on the public /u/:slug profile (no recording key). */
export interface PublicHighlight {
  id: string;
  title: string;
  description: string | null;
  sourceType: SourceType;
  scenarioKey: string | null;
  durationSeconds: number | null;
  transcriptExcerpt: string | null;
  result?: PublicResultView;
}
export type PortfolioTheme = 'default' | 'minimal' | 'bold' | 'professional';
export type MarketplacePublishStatus = 'unpublished' | 'published' | 'sync_error';
export type CertificationLevel = 'basic' | 'advanced' | 'expert';
export type CertificationSource = 'exam' | 'authority';
export type DifficultyTier = 'bronze' | 'silver' | 'gold' | 'platinum' | 'master';

export interface CustomSection {
  id: string;
  title: string;
  content: string;
  order: number;
  isVisible: boolean;
}

// Certification Types
export interface DomainCertification {
  domainKey: string;
  label: string;
  category: string;
  level: CertificationLevel;
  source: CertificationSource;
  score: number;
  issuedAt: string;
}

export interface PersonaCertification {
  personaId: string;
  personaKey: string;
  label: string;
  tier: DifficultyTier;
  completedLevels: number;
  totalLevels: number;
  isMastered: boolean;
  completedAt?: string;
}

export interface CertificationsSummary {
  domains: DomainCertification[];
  personas: PersonaCertification[];
  totals: {
    domainCertsCount: number;
    personasMasteredCount: number;
    totalNpcLevelsBeaten: number;
  };
}

// NPC Progress for Public Profile
export interface PublicNpcProgress {
  personaKey: string;
  personaLabel: string;
  tier: DifficultyTier;
  levelsPassed: number;
  totalLevels: number;
  isMastered: boolean;
}

// Visibility Settings
export interface ProfileVisibilitySettings {
  showAuthorityScores: boolean;
  showBadges: boolean;
  showFeaturedClips: boolean;
  showCertificationsPublicly: boolean;
  showNpcProgressPublicly: boolean;
}

export interface PortfolioProfile {
  id: string;
  userId: string;
  slug?: string;
  displayName?: string;
  headline?: string;
  bio?: string;
  rateType: RateType;
  rateAmount?: number;
  currency: string;
  isPublic: boolean;
  availableForHire: boolean;
  location?: string;
  languages?: string[];
  externalLink?: string;
  featuredSessionId?: string;
  customSections: CustomSection[];
  theme: PortfolioTheme;
  lastSyncedReputationAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PortfolioHighlight {
  id: string;
  portfolioId: string;
  sourceType: SourceType;
  sourceId: string;
  title: string;
  description?: string;
  scenarioKey?: string;
  audioUrl?: string;
  transcriptExcerpt?: string;
  durationSeconds?: number;
  isPublic: boolean;
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
  /** Coaching result items only (the owner's list: full provenance; public lists: PublicResultView). */
  result?: PortfolioResultProvenance | PublicResultView;
}

export interface UpdatePortfolioRequest {
  displayName?: string;
  headline?: string;
  bio?: string;
  slug?: string;
  rateType?: RateType;
  rateAmount?: number;
  currency?: string;
  isPublic?: boolean;
  availableForHire?: boolean;
  location?: string;
  languages?: string[];
  externalLink?: string;
  featuredSessionId?: string | null;
  customSections?: CustomSection[];
  theme?: PortfolioTheme;
}

export interface UpsertHighlightRequest {
  id?: string;
  sourceType: SourceType;
  sourceId: string;
  title: string;
  description?: string;
  scenarioKey?: string;
  audioUrl?: string;
  transcriptExcerpt?: string;
  durationSeconds?: number;
  isPublic?: boolean;
  orderIndex?: number;
}

export interface AuthoritySummary {
  overallAuthorityScore: number | null;
  topDomains: Array<{
    domainId: string;
    domainName: string;
    authorityScore: number;
    objectivesPassed: number;
    totalObjectives: number;
  }>;
  specialties: Array<{
    categoryId: string;
    categoryName: string;
    domainName: string;
    authorityScore: number;
  }>;
  badges: Array<{
    id: string;
    name: string;
    type: 'domain_mastery' | 'assessment_streak' | 'top_performer' | 'early_adopter';
    earnedAt: string;
    metadata?: Record<string, unknown>;
  }>;
  totalAssessments: number;
}

// Public Profile Types (for viewing others' profiles)
export interface PublicProfileResponse {
  user: {
    displayName: string;
    headline: string | null;
    avatarUrl: string | null;
    location: string | null;
  };
  hire?: {
    availableForHire: boolean;
    rateType: RateType | null;
    rateAmount: number | null;
    currency: string;
    portfolioId: string;
    userId: string; // Target user ID for hire requests
  } | null;
  authority?: {
    tier: string;
    /** The canonical average measured overall; null = not measured (never 0). */
    score: number | null;
    totalSessions: number;
  } | null;
  badges?: Array<{
    key: string;
    earnedAt: string;
  }>;
  certifications?: CertificationsSummary;
  npcProgress?: {
    personas: PublicNpcProgress[];
  };
  samples?: Array<{
    id: string;
    title: string;
    clipType: string;
    durationSeconds?: number;
    canListenPreview: boolean;
  }>;
  /** Endorsements the learner published (P4.1): endorser display name, kind, date. */
  endorsements?: PublicEndorsement[];
  /** Published portfolio items only, each result with its rubric / evidence / overall. */
  highlights?: PublicHighlight[];
}

// My Public Profile Types (for editing own profile)
export interface MyPublicProfileSettings {
  slug: string | null;
  isPublic: boolean;
  displayName: string | null;
  headline: string | null;
  bio: string | null;
  location: string | null;
  avatarUrl: string | null;
  visibility: ProfileVisibilitySettings;
  featuredClips: Array<{
    id: string;
    title: string;
    clipType: string;
    sourceId: string;
    description?: string;
    durationSeconds?: number;
    thumbnailUrl?: string;
    audioUrl?: string;
    addedAt: string;
    order: number;
  }>;
  publicUrl: string | null;
}

export interface UpdateMyPublicProfileRequest {
  slug?: string | null;
  isPublic?: boolean;
  displayName?: string | null;
  headline?: string | null;
  bio?: string | null;
  location?: string | null;
  showAuthorityScores?: boolean;
  showBadges?: boolean;
  showFeaturedClips?: boolean;
  showCertificationsPublicly?: boolean;
  showNpcProgressPublicly?: boolean;
}

// Clip Listen Types
export interface ClipPreviewResponse {
  clipId: string;
  title: string;
  previewUrl: string | null;
  previewDurationSeconds?: number;
  fullDurationSeconds?: number;
  requiresAuthForFull?: boolean;
}

export interface ClipListenResponse {
  clipId: string;
  title: string;
  audioUrl: string | null;
  durationSeconds?: number;
}

// Legacy PublicProfileDto (kept for backwards compatibility)
export interface PublicProfileDto {
  userId: string;
  publicSlug: string;
  displayName: string | null;
  headline: string | null;
  bio: string | null;
  isPublic: boolean;
  availableForHire: boolean;
  authoritySummary: {
    overallAuthorityScore: number | null;
    topDomains: Array<{
      domainId?: string;
      domainName?: string;
      authorityScore: number;
    }>;
    /** Omitted when the badge read failed: unavailable is never "no badges". */
    badges?: Array<{
      id: string;
      name: string;
      type: string;
      earnedAt: string;
    }>;
    /** The owner's own view only: badges could not be read right now. */
    badgesUnavailable?: boolean;
  };
  portfolioSettings: {
    showAuthorityScores: boolean;
    showBadges: boolean;
    showFeaturedClips: boolean;
    showCertificationsPublicly: boolean;
    showNpcProgressPublicly: boolean;
    minimumScoreToShow: number;
  };
  featuredClips: Array<{
    id: string;
    clipType: string;
    sourceId: string;
    title: string;
    description?: string;
    durationSeconds?: number;
    thumbnailUrl?: string;
    audioUrl?: string;
    addedAt: string;
    order: number;
  }>;
  certifications?: CertificationsSummary;
  npcProgress?: {
    personas: PublicNpcProgress[];
  };
  marketplaceListing: {
    id: string;
    verifiedRoles: string[];
    verifiedSpecialties: string[];
    overallScore: number | null;
    sessionCount: number;
    popularityScore: number;
  } | null;
  location: string | null;
  languages: string[] | null;
  externalLink: string | null;
  theme: string;
}

export interface ViewStats {
  totalViews: number;
  uniqueViewers: number;
  recentViews: number;
}

export interface MyPublicProfileResponse {
  profile: PublicProfileDto | null;
  canPublish: boolean;
  portfolio: PortfolioProfile;
  viewStats: ViewStats;
}

export interface PublishProfileParams {
  showAuthorityScores?: boolean;
  showBadges?: boolean;
  showFeaturedClips?: boolean;
  minimumScoreToShow?: number;
}

/**
 * The portfolio fields an unauthenticated read returns — the backend's
 * explicit allow-list (PUBLIC_PORTFOLIO_FIELDS). No sync/marketplace state,
 * owner settings or private pointers. availableForHire is true only when the
 * owner also accepts contact.
 */
export type PublicPortfolioProfile = Pick<
  PortfolioProfile,
  | 'id'
  | 'userId'
  | 'slug'
  | 'displayName'
  | 'headline'
  | 'bio'
  | 'rateType'
  | 'rateAmount'
  | 'currency'
  | 'isPublic'
  | 'availableForHire'
  | 'location'
  | 'languages'
  | 'externalLink'
  | 'customSections'
  | 'theme'
> & {
  featuredClipsJson?: Array<{ id: string; title: string; clipType?: string; durationSeconds?: number }>;
};

export interface PublicPortfolioResponse {
  portfolio: PublicPortfolioProfile;
  highlights: PortfolioHighlight[];
  verifiedRoles: string[];
  verifiedSpecialties: string[];
  badges: string[];
  /** Endorsements the learner published (P4.1): endorser display name, kind, date. */
  endorsements?: PublicEndorsement[];
  /** Omitted when the user does not show authority scores publicly. */
  stats?: {
    totalSessions: number;
    /** null = nothing measured. */
    overallAvgScore: number | null;
  };
  authoritySummary?: AuthoritySummary;
}

// Marketplace Publish State
export interface MarketplacePublishState {
  marketplaceStatus: MarketplacePublishStatus;
  marketplaceProfileId: string | null;
  publishedAt: string | null;
  lastSyncedAt: string | null;
  syncError: string | null;
  marketplaceUrl: string | null;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const portfolioApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get current user's portfolio
    getMyPortfolio: builder.query<PortfolioProfile, void>({
      query: () => '/portfolio/me',
      transformResponse: (response: ApiResponse<PortfolioProfile>) => response.data,
      providesTags: ['Portfolio'],
    }),

    // Update current user's portfolio
    updateMyPortfolio: builder.mutation<PortfolioProfile, UpdatePortfolioRequest>({
      query: (body) => ({
        url: '/portfolio/me',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiResponse<PortfolioProfile>) => response.data,
      invalidatesTags: ['Portfolio', 'Marketplace'],
    }),

    // Get current user's highlights
    getMyHighlights: builder.query<PortfolioHighlight[], void>({
      query: () => '/portfolio/me/highlights',
      transformResponse: (response: ApiResponse<PortfolioHighlight[]>) => response.data,
      providesTags: ['Highlights'],
    }),

    // Create or update a highlight
    upsertHighlight: builder.mutation<PortfolioHighlight, UpsertHighlightRequest>({
      query: (body) => ({
        url: '/portfolio/me/highlights',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<PortfolioHighlight>) => response.data,
      invalidatesTags: ['Highlights'],
    }),

    // Delete a highlight
    deleteHighlight: builder.mutation<void, string>({
      query: (id) => ({
        url: `/portfolio/me/highlights/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Highlights'],
    }),

    // Sync portfolio from reputation data
    syncMyPortfolio: builder.mutation<PortfolioProfile, void>({
      query: () => ({
        url: '/portfolio/me/sync',
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<PortfolioProfile>) => response.data,
      invalidatesTags: ['Portfolio', 'Highlights'],
    }),

    // Get public portfolio for a user
    getPublicPortfolio: builder.query<PublicPortfolioResponse, string>({
      query: (userId) => `/portfolio/${userId}`,
      transformResponse: (response: ApiResponse<PublicPortfolioResponse>) => response.data,
    }),

    // Get public portfolio by slug
    getPortfolioBySlug: builder.query<PublicPortfolioResponse, string>({
      query: (slug) => `/portfolio/slug/${slug}`,
      transformResponse: (response: ApiResponse<PublicPortfolioResponse>) => response.data,
    }),

    // =====================================================
    // Public Profile Endpoints
    // =====================================================

    // Get current user's public profile data (for editing)
    getMyPublicProfile: builder.query<MyPublicProfileResponse, void>({
      query: () => '/portfolio/me/public-profile',
      transformResponse: (response: ApiResponse<MyPublicProfileResponse>) => response.data,
      providesTags: ['PublicProfile'],
    }),

    // Publish current user's profile
    publishMyProfile: builder.mutation<PortfolioProfile, PublishProfileParams | void>({
      query: (params) => ({
        url: '/portfolio/me/publish',
        method: 'POST',
        body: params || {},
      }),
      transformResponse: (response: ApiResponse<PortfolioProfile>) => response.data,
      invalidatesTags: ['Portfolio', 'PublicProfile'],
    }),

    // Unpublish current user's profile
    unpublishMyProfile: builder.mutation<PortfolioProfile, void>({
      query: () => ({
        url: '/portfolio/me/unpublish',
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<PortfolioProfile>) => response.data,
      invalidatesTags: ['Portfolio', 'PublicProfile'],
    }),

    // Get public profile by slug (no auth required) - with certifications/NPC data
    getPublicProfileBySlug: builder.query<PublicProfileResponse, string>({
      query: (slug) => `/public-profiles/${slug}`,
      transformResponse: (response: ApiResponse<PublicProfileResponse>) => response.data,
    }),

    // =====================================================
    // Public Profile Settings Endpoints (new)
    // =====================================================

    // Get own public profile settings
    getMyPublicProfileSettings: builder.query<MyPublicProfileSettings, void>({
      query: () => '/me/public-profile',
      transformResponse: (response: ApiResponse<MyPublicProfileSettings>) => response.data,
      providesTags: ['PublicProfile'],
    }),

    // Update own public profile settings
    updateMyPublicProfileSettings: builder.mutation<MyPublicProfileSettings, UpdateMyPublicProfileRequest>({
      query: (body) => ({
        url: '/me/public-profile',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiResponse<MyPublicProfileSettings>) => response.data,
      invalidatesTags: ['PublicProfile', 'Portfolio', 'Marketplace'],
    }),

    // =====================================================
    // Clip Listen Endpoints
    // =====================================================

    // Preview a featured clip (FREE, no auth required)
    previewClip: builder.query<ClipPreviewResponse, { slug: string; clipId: string }>({
      query: ({ slug, clipId }) => `/public-profiles/${slug}/clips/${clipId}/preview`,
      transformResponse: (response: ApiResponse<ClipPreviewResponse>) => response.data,
    }),

    // Listen to a featured clip (credit-gated)
    listenToClip: builder.query<ClipListenResponse, { slug: string; clipId: string }>({
      query: ({ slug, clipId }) => `/public-profiles/${slug}/clips/${clipId}/listen`,
      transformResponse: (response: ApiResponse<ClipListenResponse>) => response.data,
    }),

    // =====================================================
    // Marketplace Publishing Endpoints
    // =====================================================

    // Get current Marketplace publish state
    getMarketplacePublishStatus: builder.query<MarketplacePublishState, void>({
      query: () => '/portfolio/me/marketplace/status',
      transformResponse: (response: ApiResponse<MarketplacePublishState>) => response.data,
      providesTags: ['MarketplacePublish'],
    }),

    // Publish to external Marketplace
    publishToMarketplace: builder.mutation<MarketplacePublishState, void>({
      query: () => ({
        url: '/portfolio/me/marketplace/publish',
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<MarketplacePublishState>) => response.data,
      invalidatesTags: ['MarketplacePublish', 'Portfolio'],
    }),

    // Unpublish from external Marketplace
    unpublishFromMarketplace: builder.mutation<MarketplacePublishState, void>({
      query: () => ({
        url: '/portfolio/me/marketplace/unpublish',
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<MarketplacePublishState>) => response.data,
      invalidatesTags: ['MarketplacePublish', 'Portfolio'],
    }),

    // Re-sync to external Marketplace
    syncToMarketplace: builder.mutation<MarketplacePublishState, void>({
      query: () => ({
        url: '/portfolio/me/marketplace/sync',
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<MarketplacePublishState>) => response.data,
      invalidatesTags: ['MarketplacePublish', 'Portfolio'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMyPortfolioQuery,
  useUpdateMyPortfolioMutation,
  useGetMyHighlightsQuery,
  useUpsertHighlightMutation,
  useDeleteHighlightMutation,
  useSyncMyPortfolioMutation,
  useGetPublicPortfolioQuery,
  useGetPortfolioBySlugQuery,
  // Public Profile hooks
  useGetMyPublicProfileQuery,
  usePublishMyProfileMutation,
  useUnpublishMyProfileMutation,
  useGetPublicProfileBySlugQuery,
  // Public Profile Settings hooks (new)
  useGetMyPublicProfileSettingsQuery,
  useUpdateMyPublicProfileSettingsMutation,
  // Clip listen hooks
  useLazyPreviewClipQuery,
  useLazyListenToClipQuery,
  useListenToClipQuery,
  // Marketplace Publishing hooks
  useGetMarketplacePublishStatusQuery,
  usePublishToMarketplaceMutation,
  useUnpublishFromMarketplaceMutation,
  useSyncToMarketplaceMutation,
} = portfolioApi;
